import { useCallback, useState } from 'react';
import { DragOverEvent, Layout } from 'react-grid-layout';
import RGL, { WidthProvider } from 'react-grid-layout';
const ReactGridLayout = WidthProvider(RGL);
import './DropGrid.scss';
import { Widget } from '../../api/dashbboards/DashboardWidgets';
import AutoWidget from './AutoWidget';
import { Flex, FlexItem, Text } from '@patternfly/react-core';
import _ from 'lodash';
import { TNewDateRange } from '../../api/types/TNewDateRange';
import { DashboardFilter } from '../../api/dashbboards/DashboardFilter';
import { TDashboardInightsEntity } from '../../api/shared-entity/SharedEntity';

type Props = {
	widgets: Widget[];
	selectedDate?: TNewDateRange;
	handleWidgetChange?: (widget: Widget) => void;
	removeWidget?: (widget: Widget) => void;
	handleEditClick?: (widget: Widget) => void;
	displayOnly?: boolean;
	isDashboard?: boolean;
	gridLayout?: boolean;
	gridBorder?: boolean;
	filters?: DashboardFilter[];
	isEdit?: boolean;
	allowClickNavigate?: boolean;
	noAnimation?: boolean;
	handleWidgetSave?: (widget: Widget) => Promise<void>;
	isEmptyDashboardFilters?: boolean;
	isInsightsOpen?: boolean;
	skipLazyLoad?: boolean;
	selectedChartWidget?: Widget;
	insightEntities?: TDashboardInightsEntity[];
};

const DropGrid = (props: Props) => {
	const {
		widgets,
		selectedDate,
		handleWidgetChange,
		removeWidget,
		handleEditClick,
		displayOnly,
		isDashboard,
		gridLayout,
		gridBorder,
		filters,
		isEdit,
		noAnimation,
		allowClickNavigate,
	} = props;
	const [currentStaticId, setCurrentStaticId] = useState<number>();
	const [layouts, setLayouts] = useState<ReactGridLayout.Layout[]>();

	const setEditingId = useCallback(
		(id?: number) => {
			setCurrentStaticId(id);
		},
		[setCurrentStaticId]
	);

	const onDrop = (
		_layout: ReactGridLayout.Layout[],
		item: ReactGridLayout.Layout,
		_e: DragEvent
	) => {
		let widgetData: Widget;
		if (_e.dataTransfer) {
			const widgetId = widgets.find((w) => w.id === widgets.length + 1)?.id;
			const rawWidgetData = _e.dataTransfer.getData('widgetData');
			const fallback = global.localStorage.getItem('zeroedIn-dragging-item');
			const source = rawWidgetData || fallback;
			if (!source) return;
			widgetData = JSON.parse(source) as Widget;
			widgetData.id = widgetId ? widgetId + 1 : widgets.length + 1; //A temp id assigned to a widget that will not be saved to the DB
			widgetData.row = item.y;
			widgetData.col = item.x;
			widgetData.isNew = true;

			handleWidgetChange && handleWidgetChange(widgetData);
		}
	};

	const onResizeStop = (
		_layout: ReactGridLayout.Layout[],
		_oldItem: ReactGridLayout.Layout,
		newItem: ReactGridLayout.Layout,
		_placeholder: ReactGridLayout.Layout,
		_e: MouseEvent,
		_element: HTMLElement
	) => {
		const widget = widgets.find((w) => w.id?.toString() === newItem.i);
		if (widget) {
			const updated = {
				...widget,
				sizey: newItem.h,
				sizex: newItem.w,
				widgetChanged: true,
			};
			handleWidgetChange?.(updated);
		}
	};

	const onDropDragOver = (_e: DragOverEvent) => {
		let widgetData: Widget;
		const draggingItem = global.localStorage.getItem('zeroedIn-dragging-item');
		if (draggingItem) {
			widgetData = JSON.parse(draggingItem) as Widget;
			return { h: widgetData.sizey, w: widgetData.sizex };
		}

		return undefined;
	};

	const onDragStop = (layout: Layout[], oldItem: Layout, newItem: Layout) => {
		const widget = widgets.find((w) => w.id?.toString() === newItem.i);
		if (widget) {
			handleWidgetChange?.({
				...widget,
				col: newItem.x,
				row: newItem.y,
				sizex: newItem.w,
				sizey: newItem.h,
				widgetChanged: true,
			});
		}
	};

	const onLayoutChange = (currentLayout: Layout[]) => {
		if (isEdit) {
			currentLayout.map((layout) => {
				const widget = widgets.find((w) => w.id?.toString() === layout.i);
				if (widget && hasWidgetChanged(widget, layout)) {
					const updated = {
						...widget,
						col: layout.x,
						row: layout.y,
						sizey: layout.h,
						sizex: layout.w,
						widgetChanged: true,
						isEdit: false,
					};
					handleWidgetChange?.(updated);
				}
			});
		}

		setLayouts(currentLayout);
	};

	const hasWidgetChanged = (currentWidget: Widget, newItem: Layout) => {
		let response = false;
		if (currentWidget.id?.toString() === newItem.i) {
			response = !_.isEqual(
				{
					col: currentWidget.col,
					row: currentWidget.row,
					sizey: currentWidget.sizey,
					sizex: currentWidget.sizex,
				},
				{
					col: newItem.x,
					row: newItem.y,
					sizey: newItem.h,
					sizex: newItem.w,
				}
			);
		}
		return response;
	};

	const getWidgetLayout = useCallback(
		(widget: Widget, index: number) => ({
			i: widget.id?.toString() ?? index,
			x: widget.col,
			y: widget.row,
			h: widget.sizey,
			w: widget.sizex,
			minW: widget.chart ? 4 : 0,
			minH: widget.chart ? 4 : 0,
			static: currentStaticId && widget.id === currentStaticId ? true : false,
		}),
		[currentStaticId]
	);

	return (
		<ReactGridLayout
			className={displayOnly ? 'layout display-only' : 'layout'}
			style={isDashboard ? undefined : { overflow: 'hidden' }}
			rowHeight={34}
			layout={layouts}
			onDrop={onDrop}
			onResizeStop={onResizeStop}
			onDropDragOver={onDropDragOver}
			onLayoutChange={onLayoutChange}
			onDragStop={onDragStop}
			isDroppable={true}
			compactType={gridLayout ? 'vertical' : null}
			isDraggable={!displayOnly}
			isResizable={!displayOnly}
			allowOverlap={!gridLayout}
			measureBeforeMount={false}
			preventCollision={widgets.length === 0}
			autoSize={isDashboard ?? false}
		>
			{widgets.length > 0 ? (
				widgets.map((widget, index) => {
					return (
						<div
							tabIndex={0}
							className={
								widget.widget_type != 'text' || !displayOnly
									? gridBorder
										? 'grid-layout-widget grid-border'
										: 'grid-layout-widget'
									: gridBorder
									? 'grid-layout-widget-text grid-border'
									: 'grid-layout-widget-text'
							}
							key={widget.id ?? index}
							data-grid={getWidgetLayout(widget, index)}
						>
							<AutoWidget
								widget={widget}
								selectedDate={selectedDate}
								removeWidget={removeWidget}
								updateWidget={handleWidgetChange}
								handleEditClick={handleEditClick}
								setEditingId={setEditingId}
								filters={filters}
								isEdit={isEdit}
								allowClickNavigate={allowClickNavigate}
								noAnimation={noAnimation}
								handleWidgetSave={props.handleWidgetSave}
								isEmptyDashboardFilters={props.isEmptyDashboardFilters}
								isInsightsOpen={props.isInsightsOpen}
								skipLazyLoad={props.skipLazyLoad}
								selectedChartWidget={props.selectedChartWidget}
								insightEntities={props.insightEntities}
							/>
						</div>
					);
				})
			) : (
				<div
					className="grid-layout-empty-widget"
					key={'0'}
					style={{ height: '100%', width: '100%' }}
				>
					<Flex
						direction={{ default: 'column' }}
						alignItems={{ default: 'alignItemsCenter' }}
						justifyContent={{ default: 'justifyContentCenter' }}
						alignSelf={{ default: 'alignSelfStretch' }}
						style={{ height: '100%' }}
					>
						<FlexItem>
							<Text component="h2">No Widgets yet</Text>
						</FlexItem>
						<FlexItem>
							<Text component="h3">Drag widgets from your assets library</Text>
						</FlexItem>
					</Flex>
				</div>
			)}
		</ReactGridLayout>
	);
};

export default DropGrid;
